import * as Auth from 'aws-amplify/auth';

import {
    allSettled,
    formatFilterParams,
    formatPagingParams,
    handleError,
} from '../common/services/helpers';
import { formatUser, formatUserResults } from './format';

import API from '/b2b/common/helpers/API';
import { API_NAME } from '#config/runtimeconfig';
import { mapFromCognitoParams } from '../authentication/services';

const allowedUserDetailsProps = [
    'name',
    'email',
    'flags',
    'firstName',
    'lastName',
    'email_verified',
    'address',
    'phone',
];

export const fetchMyUserDetails = () => {
    return API.get(API_NAME, `/me`, {}).catch(handleError).then(formatUser);
};

export const fetchUserById = userId => {
    return API.get(API_NAME, `/customer/user/${encodeURIComponent(userId)}`, {})
        .catch(handleError)
        .then(formatUser);
};

export const updateCustomerUserDetails = params => {
    const { email, ...body } = params;

    return API.patch(API_NAME, `/customer/user/${encodeURIComponent(email)}`, { body })
        .catch(handleError)
        .then(formatUser);
};

export const fetchUserDetails = async () => {
    try {
        const cognitoAttributes = await Auth.fetchUserAttributes();
        let details = {};
        try {
            details = await fetchUserById(cognitoAttributes.email);
        } catch (error) {
            // Does not have a customer details entry
        }

        return {
            ...formatUser(mapFromCognitoParams(cognitoAttributes, allowedUserDetailsProps)),
            ...details,
        };
    } catch (error) {
        handleError(error);
    }
};

export const updateUserDetails = async params => {
    // This service is used for both account and user details updates. DO NOT UPDATE COGNITO DETAILS FROM HERE
    try {
        await updateCustomerUserDetails(params);
    } catch (error) {
        handleError(error);
    }
};

export const createUser = ({ organizations, orgIds = organizations, ...params }) => {
    const options = {
        body: { orgIds, ...params },
        responseType: 'text',
    };

    return API.post(API_NAME, `/customer/user`, options)
        .then(response => {
            try {
                // Check if the response is JSON
                return JSON.parse(response);
            } catch (error) {
                return { status: 'success', code: 200, response };
            }
        })
        .catch(handleError)
        .then(responseOrId => {
            const { response: resp = responseOrId } = responseOrId;

            return typeof resp === 'object'
                ? { ...params, ...resp }
                : { id: resp || params.email, ...params };
        })
        .then(formatUser);
};

export const fetchUsers = params => {
    const options = {
        queryStringParameters: {
            ...formatFilterParams(params),
            ...formatPagingParams(params),
        },
    };

    return API.get(API_NAME, `/customer/users`, options)
        .catch(e => handleError(e, options.queryStringParameters))
        .then(formatUserResults);
};

export const deleteUser = userId => {
    return API.del(API_NAME, `/customer/user/${encodeURIComponent(userId)}`, {
        responseType: 'text',
    })
        .then(response => {
            try {
                // Check if the response is JSON
                return JSON.parse(response);
            } catch (error) {
                return { status: 'success', code: 200, response };
            }
        })
        .catch(handleError)
        .then(response => {
            const { status, response: resp } = response;
            if (status === 'success') {
                return userId;
            }

            return resp || response;
        });
};

export const deleteMultipleUsers = users => {
    return allSettled(users.map(userId => deleteUser(userId)));
};
